/// <amd-module name="VSS/Features/Identities/IdentityPickerProvider" />
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IPageData, IVssPageService } from "VSS/Platform/Page";
import { IPeoplePickerProvider } from "./IdentityPickerProvider.Props";
import {
    IIdentity,
    IdentitiesGetConnectionsResponseModel as IPersonaConnections,
    IVssIdentitySearchService,
    IVssIdentityService,
    IdentitiesSearchRequestModel,
    VssIdentityServiceName
} from "./IdentityService";

export { IPeoplePickerProvider };

/**
 * Preferred identity search service name contribution data
 */
interface IPreferredIdentitySearchServiceNameContribution {
    /**
     * Contribution ID of the identity search service
     */
    serviceId: string;
}

export class PeoplePickerProvider implements IPeoplePickerProvider {
    /**
     * Resolves to use specified identity search service or using the default service
     */
    private static async resolveIdentitySearchService(pageContext: IVssPageContext): Promise<IVssIdentitySearchService> {
        try {
            const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
            const contributions = contributionService.getContributionsByType<IPreferredIdentitySearchServiceNameContribution>(
                "ms.vss-features.preferred-identity-search-service-name"
            );
            for (const key in contributions) {
                const contribution = contributions[key];
                try {
                    const contributedService = await contributionService.getService<IVssIdentitySearchService>(contribution.serviceId);
                    if (contributedService) {
                        return contributedService;
                    }
                } catch {
                    // do nothing as contributionService.getService rejects with undefined if service not found
                }
            }
        } catch {
            // ignore errors to not block flow
        }
        return pageContext.getService<IVssIdentityService>(VssIdentityServiceName);
    }

    private readonly identityService: IVssIdentityService;
    private readonly identitySearchService: Promise<IVssIdentitySearchService>;
    private readonly pageData: IPageData;
    private _nonIdentityEntries: string[] = [];

    constructor(
        readonly pageContext: IVssPageContext,
        private defaultRequestProps?: Partial<IdentitiesSearchRequestModel>,
        private readonly filterIdentities?: (returnedEntities: IIdentity[]) => IIdentity[]
    ) {
        this.identityService = pageContext.getService<IVssIdentityService>(VssIdentityServiceName);
        this.pageData = pageContext.getService<IVssPageService>("IVssPageService").getData()!;
        this.identitySearchService = PeoplePickerProvider.resolveIdentitySearchService(pageContext);
    }

    public getAdditionalEntries = (): string[] => {
        return this._nonIdentityEntries;
    };

    public setAdditionalEntries(values: string[]) {
        this._nonIdentityEntries = values;
    }

    public updateIdentityTypes = (identityTypes: string[]) => {
        if (this.defaultRequestProps) {
            this.defaultRequestProps = {
                ...this.defaultRequestProps,
                identityTypes
            };
        } else {
            this.defaultRequestProps = {
                identityTypes
            };
        }
    };

    public addIdentitiesToMRU = (identities: IIdentity[]): Promise<boolean> => {
        return this.identityService.addMruIdentitiesAsync(identities);
    };

    public getEntitiesFromUniqueAttribute = (entityIds: string[]): IIdentity[] | PromiseLike<IIdentity[]> => {
        const query = entityIds.join(";");
        return this.identityService.searchIdentitiesAsync(
            query,
            this.defaultRequestProps?.identityTypes ?? ["user"],
            !this.pageData.isHosted ? ["ims", "source", "ad"] : ["ims", "source"],
            "uid"
        );
    };

    public getEntityFromUniqueAttribute = (entityId: string): IIdentity | PromiseLike<IIdentity> => {
        return this.identityService
            .searchIdentitiesAsync(
                entityId,
                this.defaultRequestProps?.identityTypes ?? ["user"],
                !this.pageData.isHosted ? ["ims", "source", "ad"] : ["ims", "source"],
                "uid"
            )
            .then(x => x[0]);
    };

    public onEmptyInputFocus(maxItemsCount?: number): IIdentity[] | PromiseLike<IIdentity[]> {
        return this.identityService.getIdentityMruAsync(maxItemsCount).then(identities => {
            const { defaultRequestProps } = this;
            return defaultRequestProps && defaultRequestProps.identityTypes
                ? identities.filter(
                      identity =>
                          !defaultRequestProps.identityTypes ||
                          defaultRequestProps.identityTypes.some(type => type.toLocaleLowerCase() === identity.entityType.toLocaleLowerCase())
                  )
                : identities;
        });
    }

    public onFilterIdentities(filter: string, selectedItems?: IIdentity[]): Promise<IIdentity[]> | IIdentity[] {
        if (filter === "") {
            return [];
        }
        return this.searchPersona(filter, selectedItems ? selectedItems : []);
    }

    public onRequestConnectionInformation = (
        entity: IIdentity,
        getDirectReports?: boolean
    ): IPersonaConnections | PromiseLike<IPersonaConnections> => {
        return this.identityService.getConnections(entity, getDirectReports);
    };

    public removeIdentitiesFromMRU = (identities: IIdentity[]): Promise<boolean> => {
        return this.identityService.removeMruIdentitiesAsync(identities);
    };

    private async searchPersona(searchText: string, items: IIdentity[]): Promise<IIdentity[]> {
        const searchRequest: IdentitiesSearchRequestModel = { ...this.defaultRequestProps, query: searchText };
        const identitySearchService = await this.identitySearchService;
        const identities = await identitySearchService.searchIdentitiesAsync(
            searchRequest.query,
            searchRequest.identityTypes,
            searchRequest.operationScopes,
            searchRequest.queryTypeHint,
            searchRequest.options,
            this.filterIdentities
        );

        return identities.filter(
            identity =>
                identity.active !== false && !items.some(selectedIdentity => !!selectedIdentity && selectedIdentity.entityId === identity.entityId)
        );
    }
}
